import { useDrag } from '@use-gesture/react';
import type { CSSProperties, ReactNode } from 'react';

import {
  CarouselHolder,
  CarouselSlide,
  CarouselSlider,
  CarouselSliderHolder,
  currentPageVar,
  gutterVar,
  indexVar,
  nthIndexLgVar,
  nthIndexMdVar,
  nthIndexSmVar,
  nthIndexXsVar,
  slidesPerPageLgVar,
  slidesPerPageMdVar,
  slidesPerPageSmVar,
  slidesPerPageXsVar,
} from './Carousel.styled';
import type { CarouselState } from './CarouselState';

type Props = {
  id?: string;
  state: CarouselState;
  slides: ReactNode[];
  navigation?: ReactNode;
  gutter?: number;
  height: number;
  'data-qa-id': string;
};

export function Carousel({
  id,
  state,
  'data-qa-id': qaId,
  slides,
  gutter = 8,
  navigation,
  height,
}: Props) {
  const {
    currentPage,
    responsiveSlidesPerPage,
    goToPreviousPage,
    goToNextPage,
  } = state;

  const swipeProps = useDrag(
    ({ direction: [directionX], distance: [distanceX], cancel }) => {
      // Swipe to the left
      if (directionX === -1 && distanceX > 10) {
        goToNextPage();
        cancel();
      }
      // Swipe to the right
      else if (directionX === 1 && distanceX > 10) {
        goToPreviousPage();
        cancel();
      }
    },
  )();

  return (
    <CarouselHolder id={id} data-qa-id={qaId}>
      <div
        style={{ touchAction: 'none' }}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...swipeProps}
      >
        <CarouselSliderHolder style={{ height }}>
          <CarouselSlider
            data-qa-slide-count={slides.length}
            data-qa-id={`${qaId}-slider`}
            style={
              {
                [currentPageVar]: currentPage,
                [gutterVar]: `${gutter}px`,
                [slidesPerPageXsVar]: responsiveSlidesPerPage.xs,
                [slidesPerPageSmVar]: responsiveSlidesPerPage.sm,
                [slidesPerPageMdVar]: responsiveSlidesPerPage.md,
                [slidesPerPageLgVar]: responsiveSlidesPerPage.lg,
              } as CSSProperties
            }
          >
            {slides.map((slide, index) => (
              <CarouselSlide
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                data-qa-id={`${qaId}-slide-${index}`}
                style={
                  {
                    [indexVar]: index,
                    [nthIndexXsVar]: index % responsiveSlidesPerPage.xs,
                    [nthIndexSmVar]: index % responsiveSlidesPerPage.sm,
                    [nthIndexMdVar]: index % responsiveSlidesPerPage.md,
                    [nthIndexLgVar]: index % responsiveSlidesPerPage.lg,
                  } as CSSProperties
                }
              >
                {slide}
              </CarouselSlide>
            ))}
          </CarouselSlider>
        </CarouselSliderHolder>
      </div>

      {navigation}
    </CarouselHolder>
  );
}
